import { SparkleOutline } from '@lyearn/icons';
import { Typography } from '@lyearn/ui';
import { EditorCore } from '@/components/Editor/PlainEditor/components/Editor';
import { EditorProps, ToolbarProps } from '@/components/Editor/PlainEditor/types';
import { FormCustomFieldRendererParams } from '@/components/Form';
import { FormEditorProps } from '@/components/Form/components/Editor/types';
import { PraiseMessageProps } from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseMessage/types';
import { TextTransformationEnum, TextUseCaseEnum, TransformTextInput } from '@/types/schema';

import { GradientTextProps } from '../GradientText/types';

export enum AIEditorActionTypes {
  WriteWithLyearnAI = 'WriteWithLyearnAI',
  MakeLonger = 'MakeLonger',
  MakeShorter = 'MakeShorter',
  Professional = 'Professional',
  Casual = 'Casual',
  Confident = 'Confident',
  Friendly = 'Friendly',
  ReplaceSelection = 'ReplaceSelection',
  InsertBelow = 'InsertBelow',
  Keep = 'Keep',
  Discard = 'Discard',
  TryAgain = 'TryAgain',
}

export enum AIEditorState {
  Empty = 'Empty',
  EmptyCompletion = 'EmptyCompletion',
  DraftInput = 'DraftInput',
  DraftCompletion = 'DraftComplete',
}

export interface AIEditorUseCaseProps {
  topic?: string;
  onTopicError: () => void;
  textUseCase: TextUseCaseEnum;
}

export interface AIEditorContextProps extends AIEditorUseCaseProps {
  suggestedText: string | null;
  showSuggestions: boolean;
  editorState: AIEditorState;
  isAIActive: boolean;
  showEditor: boolean;
  suggestionBodyHighlighted: boolean;
  lastTransformation: TextTransformationEnum | null;
  doTransformText: (
    transformTextInput: Pick<TransformTextInput, 'text' | 'transformation' | 'tone'>,
  ) => void;
  doTryAgain: () => void;
}

export interface AISuggestionContainerProps {
  className?: string;
  classes?: {
    root?: string;
    header: GradientTextProps['classes'];
    body?: string;
  };
  headerData?: Partial<
    Record<TextTransformationEnum, { label: string; Icon: typeof SparkleOutline }>
  >;
  showHeader?: boolean;
  showHeaderIcon?: boolean;
  headerTypographyProps?: Pick<React.ComponentProps<typeof Typography>, 'variant'>;
  bodyTypographyProps?: Pick<React.ComponentProps<typeof Typography>, 'variant' | 'color'>;
}

export interface AIBaseEditorProps extends EditorProps, AIEditorUseCaseProps {
  showEditor?: boolean; // ntt: doesnt need to pass from outside
  editorCoreProps?: Omit<
    // ntt - can make variant and omit this
    React.ComponentProps<typeof EditorCore>,
    'children' | 'showEditor' | 'disableSelection'
  >;
  editorOptions?: ToolbarProps['editorOptions']; // ntt - can use editorOptionMap and omit this prop
  editorOptionMap?: ToolbarProps['editorOptionMap'];
  classes?: {
    root?: string;
    rootActiveAI?: string;
    editor?: string;
    toolbar?: ToolbarProps['classes'];
    // ntt - can include editorCore classes here
  };
  SuggestionComponent?: React.FC;
  FeedbackComponent?: React.FC;
}

export interface AIFormEditorProps extends FormEditorProps {
  setNudgeState: FormCustomFieldRendererParams['setNudgeState'];
  topic?: string;
  textUseCase: TextUseCaseEnum;
  nudgeKey: string;
}

export interface AIPraiseMessageProps extends PraiseMessageProps, AIEditorUseCaseProps {}

export interface AIMessageInputProps extends AIEditorUseCaseProps {
  disabled?: boolean;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  placeholder: string;
  className?: string;
}
